import React from "react";
import { createPortal } from "react-dom";
import styled, { css, keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import { CircleFlag } from "react-circle-flags";
import { countries } from "country-data-list";
import {
  FiCheck,
  FiCheckSquare,
  FiDatabase,
  FiDownload,
  FiGlobe,
  FiLock,
  FiMapPin,
  FiPlus,
  FiSearch,
  FiSliders,
  FiSquare,
  FiX,
} from "react-icons/fi";
import { BrandLinkedIn, Spark } from "../components/ui/icons";
import { Tabs, Tab } from "../components/JobDetailView";
import { errorText, fetchWithAuth } from "../utils/authFetch";
import { creditsChanged } from "../utils/useCredits";
import { usePlan } from "../utils/usePlan";
import { useToast } from "../components/ToastProvider";
import { toCsv, downloadCsv, csvFileName, locationCells } from "../utils/csvExport";
import {
  PeoplePage,
  PeopleTopBar,
  Crumbs,
  Crumb,
  CrumbSep,
  PeopleCanvas,
  PeopleScreen,
  Eyebrow,
  ScreenTitle,
  ScreenSub,
  PeopleButton,
  ButtonContent,
  CandidatePhoto,
  RankAvatar,
  ActionPills,
  ActionPill,
  PillLabel,
} from "./FindCandidatesPage";

/* ─── Filter catalogue ───
   Every field is one query parameter of the people database. Main filters show from the
   start. `refine` marks filters that only narrow a search (the API rejects a search made of
   those alone): a group offers its own as "+" chips once one of its main filters is filled in.
   `after` names the filter a field must go with; it appears
   under that one automatically. `lookup` chips resolve what the user types to the value the
   API wants. */
const SECTIONS = [
  {
    id: "role",
    title: "Job",
    blurb: "What they do. The title is the strongest filter here; the rest narrows it.",
    fields: [
      { key: "title", kind: "text", label: "Job title", hint: 'e.g. "account executive", "head of sales"' },
      { key: "headline", kind: "text", label: "Headline", hint: "The line under their name, e.g. “VP Marketing”" },
      { key: "exclude_titles", kind: "chips", label: "Leave out titles", refine: true, hint: "e.g. intern, assistant" },
      { key: "description", kind: "text", label: "Role description", refine: true, hint: "What they did, e.g. “outbound prospecting”" },
      { key: "experience_min_years", kind: "int", label: "Minimum years of experience", refine: true, min: 1, max: 60 },
      { key: "function", kind: "chips", label: "Kind of work", refine: true, hint: "e.g. business development", note: "Groups different titles for the same work." },
      { key: "function_min_years", kind: "int", label: "Minimum years in that work", refine: true, after: "function", min: 1, max: 60 },
    ],
  },
  {
    id: "location",
    title: "Location",
    blurb: "Where they are based now.",
    fields: [
      { key: "geo_country_code", kind: "country", label: "Country" },
      { key: "geo_city", kind: "text", label: "City", hint: "e.g. Singapore, London" },
    ],
  },
  {
    id: "company",
    title: "Companies",
    blurb: "Where they have worked, and the shape of their career.",
    fields: [
      { key: "organization_slugs", kind: "chips", lookup: "company", label: "Worked at", hint: "Type a company name" },
      { key: "tenure", kind: "range", label: "Years at current company", minKey: "tenure_min_years", maxKey: "tenure_max_years", min: 0, max: 60, note: "Max 1 = joined within the last year." },
      { key: "company_count", kind: "range", label: "Companies in their career", minKey: "company_count_min", maxKey: "company_count_max", min: 1, max: 100 },
      { key: "current_company_count_min", kind: "int", label: "Minimum jobs held at the same time", min: 1, max: 50, note: "2 finds advisors, board members and fractional execs." },
      { key: "is_boomerang", kind: "bool", label: "Went back to a former employer" },
      { key: "exclude_organization_slugs", kind: "chips", lookup: "company", label: "Leave out", refine: true, hint: "e.g. your own firm, your clients" },
      { key: "industry", kind: "chips", label: "Industries", refine: true, hint: "e.g. financial services, media" },
      { key: "exclude_industries", kind: "chips", label: "Leave out industries", refine: true, hint: "e.g. staffing, recruiting" },
      { key: "company_size", kind: "range", label: "Company size (employees)", refine: true, minKey: "company_size_min", maxKey: "company_size_max", min: 0, max: 10000000 },
    ],
  },
  {
    id: "moves",
    title: "Job change",
    blurb: "People who moved recently, or are between jobs.",
    fields: [
      { key: "last_change_within_days", kind: "int", label: "Changed jobs within", hint: "days, e.g. 30", min: 1, max: 3650 },
      { key: "last_change_type", kind: "enum", label: "Kind of change", refine: true, after: "last_change_within_days", options: [["joined", "Started a new job"], ["left", "Left a job"], ["title_change", "New title, same employer"]] },
    ],
  },
  {
    id: "skills",
    title: "Skills & certifications",
    blurb: "What they list on their profile. Most people list few skills, so this cuts the pool hard.",
    fields: [
      { key: "skills", kind: "chips", lookup: "skill", label: "Skills", hint: "e.g. python, machine learning" },
      { key: "skills_match", kind: "enum", label: "They need", refine: true, after: "skills", options: [["any", "Any one of these skills"], ["all", "Every skill listed"]] },
      { key: "skill_count", kind: "range", label: "Skills on their profile", minKey: "skill_count_min", maxKey: "skill_count_max", min: 0, max: 500 },
      { key: "certifications", kind: "chips", label: "Certifications", hint: "e.g. AWS, PMP, CISSP" },
      { key: "certification_authority", kind: "text", label: "Issued by", hint: "e.g. Amazon Web Services, PMI" },
    ],
  },
  {
    id: "education",
    title: "Education",
    blurb: "Schools, degrees and fields of study.",
    fields: [
      { key: "institution_ids", kind: "chips", lookup: "institution", label: "School", hint: "Type a school or university" },
      { key: "education_level", kind: "enum", label: "Highest degree", options: [["phd", "PhD"], ["master", "Master's"], ["bachelor", "Bachelor's"], ["associate", "Associate"]] },
      { key: "degree", kind: "text", label: "Degree", hint: "e.g. MBA, Computer Science" },
      { key: "field_of_study", kind: "text", label: "Field of study", hint: "e.g. machine learning, law" },
    ],
  },
  {
    id: "person",
    title: "Person",
    blurb: "Find one person, or filter on their profile itself.",
    fields: [
      { key: "name", kind: "pair", label: "Name", keys: ["first_name", "last_name"], hints: ["First", "Last"] },
      { key: "summary", kind: "text", label: "About section", hint: 'e.g. "angel investor", "open to advising"' },
      { key: "primary_language", kind: "text", label: "Profile language", hint: "e.g. English, Spanish" },
      { key: "speaks_language", kind: "chips", label: "Speaks", hint: "e.g. spanish, portuguese" },
      { key: "is_creator", kind: "bool", label: "Content creator" },
      { key: "is_premium", kind: "bool", label: "Premium subscriber" },
      { key: "follower_count_min", kind: "int", label: "Minimum followers", refine: true, min: 0, max: 100000000 },
    ],
  },
];

// "Only their current job" is a setting, not a filter: it changes how these position-based filters are read.
const CURRENT_ONLY_FIELD = { key: "current_only", kind: "bool", label: "Only their current job", refine: true };
const CURRENT_ONLY_AFFECTS = ["title", "organization_slugs", "exclude_organization_slugs", "industry", "exclude_industries", "company_size_min", "company_size_max", "description", "exclude_titles"];

// Every API key with the field that owns it, for validation and the active-filter count.
const FIELD_BY_KEY = (() => {
  const out = {};
  out.current_only = CURRENT_ONLY_FIELD;
  SECTIONS.forEach((s) =>
    s.fields.forEach((f) => {
      if (f.kind === "range") {
        out[f.minKey] = { ...f, key: f.minKey };
        out[f.maxKey] = { ...f, key: f.maxKey };
      } else if (f.kind === "pair") {
        f.keys.forEach((k, i) => {
          out[k] = { ...f, kind: "text", key: k, label: `${f.label} (${f.hints[i].toLowerCase()})` };
        });
      } else out[f.key] = f;
    }),
  );
  return out;
})();

const PAGE_SIZE = 25;
const COUNTRY_OPTIONS = countries.all
  .filter((c) => c?.name && c?.alpha2 && c?.status !== "deleted" && c?.ioc !== "PRK")
  .sort((a, b) => a.name.localeCompare(b.name));

/* ─── Helpers ─── */

// Whether a filter value counts as set (an empty chip list or an unticked box does not).
function isSet(v) {
  if (v == null || v === "" || v === false) return false;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

// Chip arrays hold {name, value} for looked-up fields and strings for free ones; the API wants the values.
function chipValues(list) {
  return (list || []).map((c) => (typeof c === "string" ? c : c.value)).filter(Boolean);
}
function chipNames(list) {
  return (list || []).map((c) => (typeof c === "string" ? c : c.name)).filter(Boolean);
}

// The filter state as the request body: values only, nothing blank.
function toRequestFilters(filters) {
  const out = {};
  Object.entries(filters).forEach(([k, v]) => {
    if (!isSet(v)) return;
    out[k] = Array.isArray(v) ? chipValues(v) : v;
  });
  return out;
}

// What stops the search before it is sent; mirrors the server's rules so the message is instant.
function validate(filters) {
  const set = Object.keys(toRequestFilters(filters));
  if (set.length === 0) return "Pick at least one filter.";
  if (!set.some((k) => !FIELD_BY_KEY[k]?.refine))
    return "The filters you set only narrow a search. Add a main one as well: a job title, skill, company, country, name or school.";
  for (const k of set) {
    const f = FIELD_BY_KEY[k];
    if (f?.kind === "text" && String(filters[k]).trim().length < 3) return `${f.label}: type at least 3 characters.`;
    if (f?.kind === "chips" && chipValues(filters[k]).some((v) => v.length < (f.lookup ? 2 : 3)))
      return `${f.label}: each entry needs at least 3 characters.`;
  }
  if (set.includes("function") !== set.includes("function_min_years"))
    return "Kind of work and years doing it go together; set both.";
  if (set.includes("last_change_type") && !set.includes("last_change_within_days"))
    return "Kind of change: also fill in how many days to look back.";
  const pair = (lo, hi, label) =>
    set.includes(lo) && set.includes(hi) && Number(filters[lo]) > Number(filters[hi]) ? `${label}: the minimum is above the maximum.` : "";
  return (
    pair("company_size_min", "company_size_max", "Company employees") ||
    pair("tenure_min_years", "tenure_max_years", "Years at current company") ||
    pair("company_count_min", "company_count_max", "Number of companies") ||
    pair("skill_count_min", "skill_count_max", "Number of skills") ||
    ""
  );
}

// A one-line name for this search, saved with the people it adds to the database.
function describeFilters(filters) {
  const bits = [];
  const text = (k) => isSet(filters[k]) && bits.push(String(filters[k]).trim());
  const chips = (k) => isSet(filters[k]) && bits.push(chipNames(filters[k]).slice(0, 3).join(", "));
  text("title");
  chips("skills");
  chips("organization_slugs");
  if (isSet(filters.geo_country_code)) {
    const c = COUNTRY_OPTIONS.find((o) => o.alpha2.toLowerCase() === filters.geo_country_code);
    bits.push(c ? c.name : filters.geo_country_code.toUpperCase());
  }
  text("geo_city");
  text("headline");
  if (bits.length === 0) bits.push("custom filters");
  return `Manual search: ${bits.join(" · ")}`.slice(0, 200);
}

// Same-origin photo URL; the key mirrors the BFF (lowercased LinkedIn handle).
function photoSrc(row) {
  if (!row?.has_photo || !row?.username) return null;
  return `/api/candidate-photo/${encodeURIComponent(String(row.username).toLowerCase())}`;
}

// "Professional Summary: Dynamic and..." -> "Dynamic and..."; the label is theirs, the list has its own.
const SUMMARY_LABEL = /^\s*(professional\s+)?(summary|profile|about( me)?|overview|bio|career\s+summary|executive\s+summary)\s*[:\-–—]\s*/i;
function cleanSummary(text) {
  return String(text || "").replace(SUMMARY_LABEL, "").replace(/\s*\n\s*\n\s*/g, "\n").trim();
}

// "Fort Worth, Texas, United States" -> city + region on the row; the country is the flag.
function placeOf(row) {
  const parts = String(row.location || "").split(",").map((x) => x.trim()).filter(Boolean);
  const country = String(row.country || "").trim();
  if (parts.length > 1 && country && parts[parts.length - 1].toLowerCase() === country.toLowerCase()) parts.pop();
  return { place: parts.join(", "), country };
}

function initialsOf(name) {
  return String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

// A request-shaped filter set as one comparable string, whatever order the keys were set in.
function filtersKey(o) {
  return JSON.stringify(Object.keys(o).sort().map((k) => [k, o[k]]));
}

// A variant is a patch on the main filters: null removes a key, anything else replaces it.
function applyPatch(base, patch) {
  const out = { ...base };
  Object.entries(patch || {}).forEach(([k, v]) => {
    if (v == null) delete out[k];
    else out[k] = v;
  });
  return out;
}

// The searches one run makes: the main filters, then every variant still switched on that is valid and different. Their results are combined.
function buildLegs(filters, variants) {
  const legs = [];
  const seen = new Set();
  const push = (f, label) => {
    if (validate(f)) return;
    const req = toRequestFilters(f);
    const key = filtersKey(req);
    if (seen.has(key)) return;
    seen.add(key);
    legs.push({ filters: req, label });
  };
  push(filters, filters.title ? String(filters.title) : "Your filters");
  (variants || []).filter((v) => v.on !== false).forEach((v) => push(applyPatch(filters, v.patch), v.label));
  return legs;
}

// The chosen filters as a few words each, for the working screen.
function filterWords(filters) {
  const out = [];
  if (isSet(filters.title)) out.push(String(filters.title));
  if (isSet(filters.organization_slugs)) out.push(chipNames(filters.organization_slugs).slice(0, 3).join(", "));
  if (isSet(filters.geo_city)) out.push(String(filters.geo_city));
  if (isSet(filters.geo_country_code)) {
    const c = COUNTRY_OPTIONS.find((o) => o.alpha2.toLowerCase() === filters.geo_country_code);
    out.push(c ? c.name : String(filters.geo_country_code).toUpperCase());
  }
  if (isSet(filters.industry)) out.push(chipNames(filters.industry).slice(0, 2).join(", "));
  if (isSet(filters.skills)) out.push(chipNames(filters.skills).slice(0, 3).join(", "));
  if (isSet(filters.experience_min_years)) out.push(`${filters.experience_min_years}+ years`);
  if (isSet(filters.speaks_language)) out.push(`Speaks ${chipNames(filters.speaks_language).join(", ")}`);
  if (isSet(filters.exclude_organization_slugs)) out.push(`Not ${chipNames(filters.exclude_organization_slugs).slice(0, 2).join(", ")}`);
  if (filters.current_only === true) out.push("Current role");
  return out.filter(Boolean).slice(0, 8);
}

function rowKey(r) {
  return String(r.username || r.object_id || "").toLowerCase();
}

// The headline number climbs to its value once the search is done (about 0.7 s), instead of appearing.
function useCountUp(target, active) {
  const [n, setN] = React.useState(target);
  const fromRef = React.useRef(target);
  React.useEffect(() => {
    const from = fromRef.current;
    fromRef.current = target;
    if (!active) {
      setN(target);
      return undefined;
    }
    const reduce = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || Math.abs(target - from) <= 1) {
      setN(target);
      return undefined;
    }
    let raf = 0;
    const t0 = performance.now();
    const dur = 700;
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(from + (target - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active]);
  return n;
}

function newSearchId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
}

/* ─── Styles ─── */
const inkSoft = "var(--ink-soft)";
const inkFaint = "var(--ink-faint)";
const lineSoft = "var(--line-soft)";
const lineStrong = "var(--line-strong)";
const surfaceTint = "var(--surface-tint)";
const accentColor = "var(--accent)";
const accentSoft = "var(--accent-soft)";
const dangerColor = "var(--danger)";

const SERIF = "var(--font-display)";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: none; }
`;
const popIn = keyframes`
  0% { opacity: 0; transform: translateY(14px) scale(0.96); }
  60% { opacity: 1; transform: translateY(-2px) scale(1.01); }
  100% { opacity: 1; transform: none; }
`;
const pulse = keyframes`
  0% { transform: scale(0.6); opacity: 0.7; }
  100% { transform: scale(2.1); opacity: 0; }
`;
const breathe = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
`;

/* Before anything is searched the screen is the question and the box, nothing else. */
const Hero = styled.div`
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  padding: clamp(24px, 8vh, 96px) 0 40px;
  text-align: center;
  animation: ${fadeUp} 0.35s ease both;
  & ${ScreenTitle} {
    font-size: clamp(30px, 4.2vw, 46px);
  }
  & ${ScreenSub} {
    margin: 10px auto 26px;
    max-width: 520px;
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* The prompt: one box that takes anything pasted; the filters are set from it. */
const PromptCard = styled.div`
  position: relative;
  text-align: left;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 18px;
  padding: 16px 16px 12px;
  box-shadow: var(--shadow-lg, 0 18px 50px -24px rgba(0, 0, 0, 0.25));
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
  &:focus-within {
    border-color: ${accentColor};
    box-shadow: 0 0 0 3px ${accentSoft};
  }
`;

const HeroHint = styled.div`
  margin-top: 14px;
  font-size: 12.5px;
  color: ${inkFaint};
  & button {
    margin-left: 4px;
  }
`;

/* The wait: one breathing mark, the step it is on, and the live count climbing. */
const Working = styled.div`
  position: relative;
  max-width: 760px;
  margin: clamp(28px, 8vh, 80px) auto 0;
  padding: 24px;
  text-align: center;
  animation: ${fadeUp} 0.3s ease both;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const drift = keyframes`
  0% { transform: translate(-50%, -50%) scale(1); }
  50% { transform: translate(-46%, -54%) scale(1.12); }
  100% { transform: translate(-50%, -50%) scale(1); }
`;
const spinKf = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

/* A soft light behind the whole scene, so the wait has a stage rather than a blank page. */
const Aura = styled.div`
  position: absolute;
  left: 50%;
  top: 180px;
  width: min(900px, 120vw);
  height: 520px;
  border-radius: 50%;
  pointer-events: none;
  background: radial-gradient(closest-side, ${accentSoft} 0%, transparent 70%);
  opacity: 0.9;
  filter: blur(10px);
  animation: ${drift} 9s ease-in-out infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Scene = styled.div`
  position: relative;
  overflow: hidden;
  margin: 0 -8px;
  padding: 0 8px;
  min-height: calc(100dvh - 220px);
`;

/* What it understood: the chosen filters, arriving one by one. */
const WordRow = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 26px auto 0;
  max-width: 720px;
`;

const Word = styled.span`
  display: inline-flex;
  align-items: center;
  height: 30px;
  padding: 0 13px;
  border-radius: 999px;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${lineSoft};
  color: ${({ theme }) => theme.ink};
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  animation: ${popIn} 0.4s cubic-bezier(0.2, 0.8, 0.2, 1) both;
  animation-delay: ${({ $i }) => `${0.08 * $i}s`};
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* Every search this run makes, with its state and how many new people it added. */
const LegList = styled.div`
  width: 100%;
  max-width: 560px;
  margin: 34px auto 0;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LegRow = styled.div`
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 12px;
  background: ${({ $state, theme }) => ($state === "running" ? theme.surface : "transparent")};
  box-shadow: ${({ $state }) => ($state === "running" ? "0 1px 3px rgba(0, 0, 0, 0.06)" : "none")};
  color: ${({ $state, theme }) => ($state === "waiting" ? inkFaint : theme.ink)};
  font-size: 13.5px;
  transition:
    background 0.2s,
    color 0.2s;
  animation: ${fadeUp} 0.35s ease both;
  animation-delay: ${({ $i }) => `${0.06 * $i}s`};
  & > span:nth-child(2) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  & > span:last-child {
    font-variant-numeric: tabular-nums;
    font-size: 12.5px;
    color: ${({ $state }) => ($state === "done" ? inkSoft : inkFaint)};
    & b {
      color: ${({ theme }) => theme.ink};
      font-weight: 600;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const LegMark = styled.span`
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${({ $state }) => ($state === "done" ? accentSoft : "transparent")};
  color: ${({ $state }) => ($state === "done" ? accentColor : $state === "failed" ? dangerColor : inkFaint)};
  & > i {
    width: 13px;
    height: 13px;
    border-radius: 50%;
    border: 2px solid ${lineStrong};
    border-top-color: ${accentColor};
    animation: ${spinKf} 0.9s linear infinite;
    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }
  & > b {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${lineStrong};
  }
`;

/* The people found so far, as faces arriving. */
const Crowd = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  max-width: 760px;
  margin: 34px auto 0;
  padding: 0 12px 40px;
`;

const Face = styled.div`
  width: 36px;
  height: 36px;
  animation: ${popIn} 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) both;
  animation-delay: ${({ $i }) => `${Math.min(0.04 * $i, 0.9)}s`};
  & > * {
    width: 36px;
    height: 36px;
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Orb = styled.div`
  position: relative;
  width: 64px;
  height: 64px;
  margin: 0 auto 22px;
  display: grid;
  place-items: center;
  & > span {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 1.5px solid ${accentColor};
    animation: ${pulse} 2.2s ease-out infinite;
  }
  & > span:nth-child(2) {
    animation-delay: 0.7s;
  }
  & > span:nth-child(3) {
    animation-delay: 1.4s;
  }
  & > i {
    position: relative;
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: ${accentSoft};
    color: ${accentColor};
    animation: ${breathe} 2.2s ease-in-out infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    & > span,
    & > i {
      animation: none;
    }
  }
`;

const WorkingStep = styled.div`
  font-family: ${SERIF};
  font-size: clamp(22px, 3vw, 30px);
  line-height: 1.15;
  color: ${({ theme }) => theme.ink};
  min-height: 1.2em;
  animation: ${fadeUp} 0.25s ease both;
`;

/* The paste, in one quiet line, so the screen says what it is working on. */
const Quote = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  font-size: 12.5px;
  color: ${inkFaint};
  & span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 560px;
  }
  & svg {
    flex-shrink: 0;
  }
`;

const WorkingCount = styled.div`
  margin-top: 10px;
  font-size: 14px;
  color: ${inkSoft};
  font-variant-numeric: tabular-nums;
  & b {
    color: ${({ theme }) => theme.ink};
    font-weight: 600;
  }
`;

/* The reveal: how many turned up, who they are, and the one way into the filters. */
const Reveal = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px 24px;
  flex-wrap: wrap;
  margin: 18px 0 18px;
  animation: ${popIn} 0.45s cubic-bezier(0.2, 0.8, 0.2, 1) both;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const BigCount = styled.div`
  font-family: ${SERIF};
  font-weight: 400;
  font-size: clamp(34px, 5vw, 56px);
  line-height: 1;
  letter-spacing: -0.015em;
  color: ${({ theme }) => theme.ink};
  font-variant-numeric: tabular-nums;
  & small {
    font-size: 0.42em;
    letter-spacing: 0;
    margin-left: 10px;
    color: ${inkSoft};
  }
`;

const RevealSub = styled.div`
  margin-top: 10px;
  font-size: 14px;
  line-height: 1.5;
  color: ${inkSoft};
  max-width: 640px;
  & small {
    display: block;
    font-size: 12.5px;
    color: ${inkFaint};
  }
`;

const RevealActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const FadeList = styled.div`
  animation: ${fadeUp} 0.4s 0.15s ease both;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* ─── Filters modal ─── */
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: var(--scrim, rgba(0, 0, 0, 0.45));
  backdrop-filter: blur(2px);
  display: grid;
  place-items: center;
  padding: 40px 24px;
  @media (max-width: 640px) {
    padding: 12px;
  }
`;

const Dialog = styled.div`
  width: 100%;
  max-width: 980px;
  height: min(780px, calc(100dvh - 48px));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 20px;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  color: ${({ theme }) => theme.ink};
  box-shadow: var(--shadow-lg, 0 30px 80px -30px rgba(0, 0, 0, 0.45));
  animation: ${popIn} 0.25s ease both;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
  @media (max-width: 640px) {
    height: calc(100dvh - 24px);
    border-radius: 16px;
  }
`;

/* Left: the groups. Right: the one group picked, nothing else. */
const DialogSplit = styled.div`
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto minmax(0, 1fr);
  }
`;

const Nav = styled.nav`
  border-right: 1px solid ${lineSoft};
  background: ${surfaceTint};
  padding: 12px 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow-y: auto;
  @media (max-width: 760px) {
    border-right: 0;
    border-bottom: 1px solid ${lineSoft};
    flex-direction: row;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 10px 12px;
    gap: 6px;
  }
`;

const NavLabel = styled.div`
  padding: 8px 12px 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${inkFaint};
  @media (max-width: 760px) {
    display: none;
  }
`;

const NavItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 9px 12px;
  border: 0;
  border-radius: 10px;
  background: ${({ $active, theme }) => ($active ? theme.surface : "transparent")};
  box-shadow: ${({ $active }) => ($active ? "0 1px 2px rgba(0, 0, 0, 0.06)" : "none")};
  color: ${({ $active, theme }) => ($active ? theme.ink : inkSoft)};
  font: inherit;
  font-size: 13.5px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  text-align: left;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 0.12s,
    color 0.12s;
  &:hover {
    color: ${({ theme }) => theme.ink};
    background: ${({ $active, theme }) => ($active ? theme.surface : accentSoft)};
  }
  @media (max-width: 760px) {
    width: auto;
    padding: 7px 12px;
  }
`;

const Pane = styled.div`
  min-height: 0;
  overflow-y: auto;
  padding: 26px 30px 30px;
  @media (max-width: 640px) {
    padding: 18px 16px 22px;
  }
`;

const PaneHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
  & h4 {
    margin: 0;
    font-family: ${SERIF};
    font-weight: 400;
    font-size: 24px;
    line-height: 1.1;
  }
  & p {
    margin: 6px 0 0;
    font-size: 13px;
    line-height: 1.5;
    color: ${inkSoft};
    max-width: 520px;
  }
`;

const PaneFields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px 22px;
  align-items: start;
  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const PaneItem = styled.div`
  grid-column: ${({ $wide }) => ($wide ? "1 / -1" : "auto")};
  position: relative;
  min-width: 0;
`;

/* Narrowing filters sit in their own block under the main ones, so the pane reads main-first. */
const PaneMore = styled.div`
  margin-top: 26px;
  padding-top: 18px;
  border-top: 1px dashed ${lineSoft};
`;

const PaneMoreTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${inkFaint};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 10px;
`;

const FootSummary = styled.span`
  font-size: 13px;
  color: ${inkSoft};
  & b {
    color: ${({ theme }) => theme.ink};
    font-weight: 600;
  }
`;

const DialogHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px 14px;
  border-bottom: 1px solid ${lineSoft};
  & h3 {
    margin: 0;
    font-family: ${SERIF};
    font-weight: 400;
    font-size: 22px;
    display: inline-flex;
    align-items: center;
    gap: 10px;
  }
`;

const CloseBtn = styled.button`
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 99px;
  background: transparent;
  color: ${inkSoft};
  cursor: pointer;
  &:hover {
    background: ${surfaceTint};
    color: ${({ theme }) => theme.ink};
  }
`;

const DialogFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 20px 14px;
  border-top: 1px solid ${lineSoft};
`;

const VariantList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const PromptArea = styled.textarea`
  display: block;
  width: 100%;
  box-sizing: border-box;
  border: 0;
  outline: none;
  background: transparent;
  color: ${({ theme }) => theme.ink};
  font: inherit;
  font-size: 16px;
  line-height: 1.5;
  resize: none;
  min-height: 56px;
  max-height: 260px;
  padding: 2px 4px;
  &::placeholder {
    color: ${inkFaint};
  }
  &[disabled] {
    opacity: 0.7;
  }
`;

const PromptFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px 14px;
  flex-wrap: wrap;
  margin-top: 8px;
  font-size: 12px;
  color: ${inkFaint};
  & kbd {
    font: inherit;
    font-size: 11px;
    padding: 1px 5px;
    border-radius: 5px;
    border: 1px solid ${lineSoft};
    background: ${surfaceTint};
    color: ${inkSoft};
  }
`;

/* A variant: one more search the run makes, switchable off and removable. */
const VariantChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 5px 0 8px;
  border-radius: 999px;
  border: 1px dashed ${({ $on }) => ($on ? accentColor : lineStrong)};
  background: ${({ $on }) => ($on ? accentSoft : "transparent")};
  color: ${({ $on }) => ($on ? "var(--accent-ink)" : inkFaint)};
  font-size: 12.5px;
  max-width: 100%;
  & > button:first-child {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    padding: 0;
    cursor: pointer;
    text-decoration: ${({ $on }) => ($on ? "none" : "line-through")};
    max-width: 240px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  & > button:last-child {
    display: grid;
    place-items: center;
    width: 18px;
    height: 18px;
    border: 0;
    border-radius: 99px;
    background: transparent;
    color: inherit;
    opacity: 0.7;
    cursor: pointer;
    padding: 0;
    &:hover {
      opacity: 1;
      background: ${({ theme }) => theme.surface};
    }
  }
  & button {
    @media (max-width: 450px) {
      min-height: 0;
    }
  }
`;

const CountBadge = styled.span`
  display: inline-grid;
  place-items: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: ${accentSoft};
  color: var(--accent-ink);
  font-size: 11px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

const LinkBtn = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  color: ${accentColor};
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
  &[disabled] {
    color: ${inkFaint};
    cursor: default;
    text-decoration: none;
  }
`;

const GroupClear = styled(LinkBtn)`
  font-size: 12px;
  font-weight: 500;
  color: ${inkFaint};
  &:hover {
    color: ${accentColor};
  }
`;

const unfoldIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

const AddChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  height: 24px;
  padding: 0 9px 0 7px;
  border-radius: 999px;
  border: 1px dashed ${lineStrong};
  background: transparent;
  color: ${inkSoft};
  font: inherit;
  font-size: 12px;
  cursor: pointer;
  transition: border-color 0.12s, color 0.12s, background 0.12s;
  &:hover {
    border-style: solid;
    border-color: ${accentColor};
    color: ${accentColor};
    background: ${accentSoft};
  }
  & svg {
    flex-shrink: 0;
  }
  @media (max-width: 450px) {
    min-height: 0;
  }
`;

/* The current-job setting sits above the groups, since it changes how several of them are read. */
const RailSwitch = styled.div`
  margin: 8px 16px 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${surfaceTint};
  animation: ${unfoldIn} 0.18s ease both;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const NarrowRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  &:first-child {
    margin-top: 0;
  }
  animation: ${unfoldIn} 0.18s ease both;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* A narrowing filter the user added: removable, and marked by a thin rule so it reads as optional. */
const UnfoldItem = styled.div`
  position: relative;
  padding-right: 22px;
  padding-left: 10px;
  border-left: 2px solid ${accentSoft};
  animation: ${unfoldIn} 0.18s ease both;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const RemoveField = styled.button`
  position: absolute;
  top: -2px;
  right: -4px;
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 99px;
  background: transparent;
  color: ${inkFaint};
  cursor: pointer;
  padding: 0;
  &:hover {
    color: ${({ theme }) => theme.ink};
    background: ${surfaceTint};
  }
  @media (max-width: 450px) {
    min-height: 0;
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
`;

const FieldLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
  color: ${inkSoft};
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 6px;
`;


const FieldNote = styled.span`
  font-size: 11.5px;
  color: ${inkFaint};
  line-height: 1.4;
`;

const inputBase = css`
  width: 100%;
  box-sizing: border-box;
  height: 34px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.line};
  background: var(--surface);
  color: ${({ theme }) => theme.ink};
  font: inherit;
  font-size: 13px;
  &::placeholder {
    color: ${inkFaint};
  }
  &:focus {
    outline: none;
    border-color: ${accentColor};
    box-shadow: 0 0 0 3px ${accentSoft};
  }
`;

const Input = styled.input`
  ${inputBase}
  &[type="number"] {
    -moz-appearance: textfield;
  }
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const Select = styled.select`
  ${inputBase}
  padding-right: 28px;
  appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, ${inkSoft} 50%),
    linear-gradient(135deg, ${inkSoft} 50%, transparent 50%);
  background-position:
    calc(100% - 15px) 15px,
    calc(100% - 10px) 15px;
  background-size:
    5px 5px,
    5px 5px;
  background-repeat: no-repeat;
`;

const RangeRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 8px;
  & > span {
    font-size: 12px;
    color: ${inkFaint};
  }
`;

const Toggle = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 13px;
  color: ${({ theme }) => theme.ink};
  & input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }
`;

const Switch = styled.span`
  position: relative;
  width: 32px;
  height: 18px;
  border-radius: 999px;
  background: ${({ $on }) => ($on ? accentColor : lineStrong)};
  transition: background 0.15s;
  flex-shrink: 0;
  &::after {
    content: "";
    position: absolute;
    top: 2px;
    left: ${({ $on }) => ($on ? "16px" : "2px")};
    width: 14px;
    height: 14px;
    border-radius: 99px;
    background: #fff;
    transition: left 0.15s;
  }
`;

const ChipWrap = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  min-height: 34px;
  padding: 4px 6px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.line};
  background: var(--surface);
  &:focus-within {
    border-color: ${accentColor};
    box-shadow: 0 0 0 3px ${accentSoft};
  }
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  padding: 3px 6px 3px 9px;
  border-radius: 999px;
  background: ${accentSoft};
  color: var(--accent-ink);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  & button {
    display: inline-grid;
    place-items: center;
    width: 16px;
    height: 16px;
    border: 0;
    border-radius: 99px;
    padding: 0;
    background: transparent;
    color: inherit;
    cursor: pointer;
    &:hover {
      background: rgba(0, 0, 0, 0.08);
    }
    @media (max-width: 450px) {
      min-height: 0;
    }
  }
`;

const ChipInput = styled.input`
  flex: 1;
  min-width: 90px;
  height: 24px;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.ink};
  font: inherit;
  font-size: 13px;
  padding: 0 4px;
  &:focus {
    outline: none;
  }
  &::placeholder {
    color: ${inkFaint};
  }
`;

const Suggest = styled.div`
  position: absolute;
  left: -1px;
  right: -1px;
  top: calc(100% + 4px);
  z-index: 5;
  background: var(--surface);
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 10px;
  box-shadow: var(--shadow-md);
  overflow: hidden;
`;

const SuggestRow = styled.button`
  width: 100%;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 12px;
  border: 0;
  background: ${({ $hl }) => ($hl ? surfaceTint : "transparent")};
  font: inherit;
  font-size: 13px;
  color: ${({ theme }) => theme.ink};
  text-align: left;
  cursor: pointer;
  &:hover {
    background: ${surfaceTint};
  }
  & small {
    color: ${inkFaint};
    font-size: 11.5px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 45%;
  }
  @media (max-width: 450px) {
    min-height: 0;
  }
`;

const SuggestNote = styled.div`
  padding: 8px 12px;
  font-size: 12px;
  color: ${inkFaint};
`;

const ErrorText = styled.div`
  font-size: 12.5px;
  color: ${dangerColor};
  line-height: 1.45;
`;

const ResultsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  min-height: 32px;
  margin-bottom: 10px;
  font-size: 13px;
  color: ${inkSoft};
  & b {
    color: ${({ theme }) => theme.ink};
    font-weight: 600;
  }
`;

const BulkBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 8px 12px;
  margin-bottom: 10px;
  border-radius: 10px;
  background: ${accentSoft};
  color: var(--accent-ink);
  font-size: 13px;
  font-weight: 600;
`;

const List = styled.div`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 14px;
  overflow: hidden;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 28px 1fr auto;
  gap: 12px;
  align-items: start;
  padding: 14px 16px;
  border-bottom: 1px solid ${lineSoft};
  &:last-child {
    border-bottom: 0;
  }
  ${({ $picked }) =>
    $picked &&
    css`
      background: ${surfaceTint};
    `}
  @media (max-width: 560px) {
    grid-template-columns: 28px 1fr;
    & > :last-child {
      grid-column: 2;
    }
  }
`;

const CheckBtn = styled.button`
  display: grid;
  place-items: center;
  width: 28px;
  height: 36px;
  border: 0;
  background: transparent;
  color: ${({ $on }) => ($on ? accentColor : inkFaint)};
  cursor: pointer;
  padding: 0;
  &:hover {
    color: ${accentColor};
  }
  @media (max-width: 450px) {
    min-height: 0;
  }
`;

const Person = styled.div`
  display: flex;
  gap: 12px;
  min-width: 0;
`;

const PersonText = styled.div`
  min-width: 0;
  flex: 1;
`;

const Name = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.ink};
  text-decoration: none;
  line-height: 1.3;
  overflow-wrap: anywhere;
  & svg {
    color: ${inkFaint};
    flex-shrink: 0;
  }
  &:hover {
    color: ${accentColor};
  }
`;

const Meta = styled.div`
  font-size: 13px;
  line-height: 1.4;
  color: ${inkSoft};
  margin-top: 1px;
  overflow-wrap: anywhere;
  & b {
    font-weight: 500;
    color: ${({ theme }) => theme.ink};
  }
`;

const Facts = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 7px;
`;

const Fact = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 22px;
  padding: 0 8px 0 7px;
  border-radius: 999px;
  background: ${surfaceTint};
  color: ${inkSoft};
  font-size: 12px;
  line-height: 1;
  max-width: 100%;
  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  & > svg,
  & > img {
    flex-shrink: 0;
  }
  & > svg {
    color: ${inkFaint};
  }
  & > img {
    border-radius: 50%;
  }
  & i {
    font-style: normal;
    color: ${inkFaint};
  }
`;

const Summary = styled.div`
  margin-top: 8px;
  font-size: 13px;
  line-height: 1.55;
  color: ${inkSoft};
  white-space: pre-line;
  max-width: 72ch;
  ${({ $open }) =>
    !$open &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    `}
`;

const MoreLess = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  margin-top: 3px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: ${inkFaint};
  cursor: pointer;
  &:hover {
    color: ${accentColor};
  }
  @media (max-width: 450px) {
    min-height: 0;
  }
`;

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 4px;
`;

const Foot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 18px 0 8px;
  font-size: 12.5px;
  color: ${inkFaint};
`;

/* ─── Controls ─── */

function BoolField({ field, value, onChange }) {
  const on = value === true;
  return (
    <Field as="div">
      <Toggle>
        <input type="checkbox" checked={on} onChange={(e) => onChange(e.target.checked ? true : null)} />
        <Switch $on={on} aria-hidden />
        <span>{field.label}</span>
        
      </Toggle>
      {field.note && <FieldNote>{field.note}</FieldNote>}
    </Field>
  );
}

function NumberInput({ value, onChange, min, max, placeholder }) {
  return (
    <Input
      type="number"
      inputMode="numeric"
      min={min}
      max={max}
      placeholder={placeholder}
      value={value ?? ""}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v === "" ? null : Math.max(min, Math.min(max, Math.trunc(Number(v)))));
      }}
    />
  );
}

/* Chips: Enter or comma commits what's typed; a `lookup` kind asks the server for the
   matching companies / skills / schools and stores the value the API wants. */
function ChipsField({ field, value, onChange }) {
  const list = value || [];
  const [draft, setDraft] = React.useState("");
  const [hits, setHits] = React.useState(null); // null = nothing asked yet
  const [hl, setHl] = React.useState(0);
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);
  const reqRef = React.useRef(0);
  const atMax = list.length >= 20;

  const commit = (item) => {
    const entry = typeof item === "string" ? item.trim() : item;
    const v = typeof entry === "string" ? entry : entry.value;
    if (!v || atMax) return;
    if (chipValues(list).includes(v)) {
      setDraft("");
      return;
    }
    onChange([...list, entry]);
    setDraft("");
    setHits(null);
    setOpen(false);
  };

  React.useEffect(() => {
    if (!field.lookup) return undefined;
    const q = draft.trim();
    if (q.length < 2) {
      setHits(null);
      return undefined;
    }
    const id = ++reqRef.current;
    const t = setTimeout(async () => {
      try {
        const res = await fetchWithAuth(`/api/people-search/lookup?kind=${field.lookup}&q=${encodeURIComponent(q)}`);
        const data = res.ok ? await res.json() : { items: [] };
        if (id !== reqRef.current) return;
        setHits(Array.isArray(data.items) ? data.items : []);
        setHl(0);
        setOpen(true);
      } catch {
        if (id === reqRef.current) setHits([]);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [draft, field.lookup]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (field.lookup && open && hits && hits[hl]) commit(hits[hl]);
      else if (!field.lookup) commit(draft);
      else if (hits && hits.length === 0 && draft.trim()) commit(draft.trim());
    } else if (e.key === "ArrowDown" && hits?.length) {
      e.preventDefault();
      setHl((h) => Math.min(hits.length - 1, h + 1));
    } else if (e.key === "ArrowUp" && hits?.length) {
      e.preventDefault();
      setHl((h) => Math.max(0, h - 1));
    } else if (e.key === "Backspace" && !draft && list.length) {
      onChange(list.slice(0, -1));
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <Field as="div">
      <FieldLabel>
        {field.label}
        
      </FieldLabel>
      <ChipWrap ref={wrapRef} onClick={() => wrapRef.current?.querySelector("input")?.focus()}>
        {list.map((c, i) => {
          const name = typeof c === "string" ? c : c.name;
          const v = typeof c === "string" ? c : c.value;
          return (
            <Chip key={v} title={typeof c === "string" ? undefined : c.value}>
              <span>{name}</span>
              <button
                type="button"
                aria-label={`Remove ${name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(list.filter((_, j) => j !== i));
                }}
              >
                <FiX size={11} />
              </button>
            </Chip>
          );
        })}
        {!atMax && (
          <ChipInput
            value={draft}
            placeholder={list.length ? "" : field.hint || "Type and press Enter"}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKey}
            onFocus={() => hits && setOpen(true)}
            onBlur={() => {
              // Free chips keep what was typed; looked-up ones need a pick, so the draft stays for the next focus.
              if (!field.lookup && draft.trim()) commit(draft);
            }}
          />
        )}
        {field.lookup && open && hits && (
          <Suggest>
            {hits.length === 0 ? (
              <SuggestNote>Nothing matched. Press Enter to use what you typed.</SuggestNote>
            ) : (
              hits.map((h, i) => (
                <SuggestRow key={h.value} type="button" $hl={i === hl} onMouseDown={(e) => e.preventDefault()} onClick={() => commit(h)}>
                  <span>{h.name}</span>
                  {h.hint && h.hint !== h.name && <small>{h.hint}</small>}
                </SuggestRow>
              ))
            )}
          </Suggest>
        )}
      </ChipWrap>
      {field.note && <FieldNote>{field.note}</FieldNote>}
    </Field>
  );
}

function CountryField({ field, value, onChange }) {
  return (
    <Field>
      <FieldLabel>
        {field.label}
        {value && <CircleFlag countryCode={value} height={14} />}
      </FieldLabel>
      <Select value={value || ""} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">Anywhere</option>
        {COUNTRY_OPTIONS.map((c) => (
          <option key={c.alpha2} value={c.alpha2.toLowerCase()}>
            {c.name}
          </option>
        ))}
      </Select>
    </Field>
  );
}

function FilterField({ field, filters, setFilter }) {
  if (field.kind === "bool") return <BoolField field={field} value={filters[field.key]} onChange={(v) => setFilter(field.key, v)} />;
  if (field.kind === "chips") return <ChipsField field={field} value={filters[field.key]} onChange={(v) => setFilter(field.key, v)} />;
  if (field.kind === "country") return <CountryField field={field} value={filters[field.key]} onChange={(v) => setFilter(field.key, v)} />;
  if (field.kind === "pair") {
    return (
      <Field as="div">
        <FieldLabel>{field.label}</FieldLabel>
        <RangeRow style={{ gridTemplateColumns: "1fr 1fr" }}>
          {field.keys.map((k, i) => (
            <Input key={k} value={filters[k] || ""} placeholder={field.hints[i]} onChange={(e) => setFilter(k, e.target.value || null)} />
          ))}
        </RangeRow>
      </Field>
    );
  }
  if (field.kind === "range") {
    return (
      <Field as="div">
        <FieldLabel>
          {field.label}
          
        </FieldLabel>
        <RangeRow>
          <NumberInput value={filters[field.minKey]} onChange={(v) => setFilter(field.minKey, v)} min={field.min} max={field.max} placeholder="Min" />
          <span>to</span>
          <NumberInput value={filters[field.maxKey]} onChange={(v) => setFilter(field.maxKey, v)} min={field.min} max={field.max} placeholder="Max" />
        </RangeRow>
      </Field>
    );
  }
  return (
    <Field>
      <FieldLabel>
        {field.label}
        
      </FieldLabel>
      {field.kind === "enum" ? (
        <Select value={filters[field.key] || ""} onChange={(e) => setFilter(field.key, e.target.value || null)}>
          <option value="">Any</option>
          {field.options.map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </Select>
      ) : field.kind === "int" ? (
        <NumberInput value={filters[field.key]} onChange={(v) => setFilter(field.key, v)} min={field.min} max={field.max} placeholder={field.hint} />
      ) : (
        <Input value={filters[field.key] || ""} placeholder={field.hint} onChange={(e) => setFilter(field.key, e.target.value || null)} />
      )}
      {field.note && <FieldNote>{field.note}</FieldNote>}
    </Field>
  );
}

function fieldKeys(f) {
  if (f.kind === "range") return [f.minKey, f.maxKey];
  if (f.kind === "pair") return f.keys;
  return [f.key];
}

function fieldIsSet(f, filters) {
  return fieldKeys(f).some((k) => isSet(filters[k]));
}

// Whether one of the group's own main filters is filled in: that is what lets its narrowing filters show.
function groupHasMain(section, filters) {
  return section.fields.some((f) => !f.refine && fieldIsSet(f, filters));
}

// What a group renders: every main filter, plus narrowing ones the user picked (once a main filter exists) or that hold a value, plus a paired field once its partner is set.
function groupFields(section, filters, shown, hasMain) {
  return section.fields.filter((f) => {
    if (!f.refine) return true;
    if (fieldIsSet(f, filters)) return true;
    if (f.after) return isSet(filters[f.after]);
    return shown.has(f.key) && hasMain;
  });
}

function sectionCount(section, filters) {
  return section.fields.reduce((n, f) => n + fieldKeys(f).filter((k) => isSet(filters[k])).length, 0);
}

/* ─── Page ─── */

export default function ManualSearchPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { features: planFeatures } = usePlan();
  // Saving people to the database is Outreach, which the free trial doesn't have.
  const actionsLocked = planFeatures.outreach === false;

  const [filters, setFilters] = React.useState({});
  const [shown, setShown] = React.useState(() => new Set()); // optional fields the user added to a group
  const [rows, setRows] = React.useState([]);
  const [hasMore, setHasMore] = React.useState(false);
  const [searched, setSearched] = React.useState(false);
  const [loading, setLoading] = React.useState(false); // first page
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState("");
  const [savedIds, setSavedIds] = React.useState(() => new Set());
  const [busyIds, setBusyIds] = React.useState(() => new Set());
  const [picked, setPicked] = React.useState(() => new Set());
  const [openSummaries, setOpenSummaries] = React.useState(() => new Set());
  const [prompt, setPrompt] = React.useState("");
  const [suggesting, setSuggesting] = React.useState(false); // the AI is choosing filters
  const [filtersOpen, setFiltersOpen] = React.useState(false); // the filters modal
  const [modalTab, setModalTab] = React.useState("role"); // which group the modal shows
  const [stepTick, setStepTick] = React.useState(0); // rotates the wording while the AI reads
  const [aiSummary, setAiSummary] = React.useState("");
  const [variants, setVariants] = React.useState([]); // [{label, patch, on}] from the AI: extra searches combined into the list
  const [legProgress, setLegProgress] = React.useState({ done: 0, total: 0 });
  const [legStats, setLegStats] = React.useState([]); // [{label, count, state: waiting|running|done|failed}] while a search runs
  const [unresolved, setUnresolved] = React.useState([]);
  const promptRef = React.useRef(null);
  const searchIdRef = React.useRef("");
  const legsRef = React.useRef([]); // [{filters, cursor, hasMore}] of the running search
  const seenRef = React.useRef(new Set()); // row keys already listed, across legs and pages
  const titleRef = React.useRef("");

  const requestFilters = toRequestFilters(filters);
  const activeCount = Object.keys(requestFilters).length;
  const legsBusy = legProgress.done < legProgress.total;
  // Filters or variants changed since the last search: the strip offers "Search again".
  const dirty =
    searched &&
    !loading &&
    !suggesting &&
    !legsBusy &&
    legsRef.current.length > 0 &&
    JSON.stringify(buildLegs(filters, variants).map((l) => filtersKey(l.filters))) !== JSON.stringify(legsRef.current.map((l) => filtersKey(l.filters)));
  const setFilter = React.useCallback((key, v) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (isSet(v)) next[key] = v;
      else delete next[key];
      return next;
    });
  }, []);

  const openFilters = () => {
    const withValue = SECTIONS.find((sec) => sectionCount(sec, filters) > 0);
    setModalTab(variants.length > 0 ? "variants" : withValue ? withValue.id : "role");
    setFiltersOpen(true);
  };

  React.useEffect(() => {
    if (!filtersOpen) return undefined;
    const onKey = (e) => e.key === "Escape" && setFiltersOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [filtersOpen]);

  React.useEffect(() => {
    if (!suggesting) return undefined;
    setStepTick(0);
    const t = setInterval(() => setStepTick((n) => n + 1), 2200);
    return () => clearInterval(t);
  }, [suggesting]);

  // The prompt box grows with what is pasted, up to its max height.
  React.useEffect(() => {
    const el = promptRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 260)}px`;
  }, [prompt]);

  // Who is already in the database, so the pills open in the right state.
  React.useEffect(() => {
    if (actionsLocked) return;
    let alive = true;
    fetchWithAuth("/api/outreach/external-shortlist/ids")
      .then((r) => (r.ok ? r.json() : { ids: [] }))
      .then((d) => alive && setSavedIds(new Set(Array.isArray(d.ids) ? d.ids : [])))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [actionsLocked]);

  const fetchPage = React.useCallback(async (body) => {
    const res = await fetchWithAuth("/api/people-search/manual", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data?.error || "Could not run the search right now. Please try again.");
      err.code = data?.code;
      err.status = res.status;
      throw err;
    }
    creditsChanged();
    return data;
  }, []);

  // Append rows not listed yet (the same person often answers several variants).
  const appendRows = (incoming) => {
    const fresh = (Array.isArray(incoming) ? incoming : []).filter((r) => {
      const k = rowKey(r);
      if (!k || seenRef.current.has(k)) return false;
      seenRef.current.add(k);
      return true;
    });
    if (fresh.length) setRows((prev) => [...prev, ...fresh]);
    return fresh.length;
  };

  // One run = the main filters plus every variant, searched one after the other (the database wants about a call a second); the first page paints at once and the rest stream in.
  const runSearch = async (useFilters = filters, useVariants = variants, searchId = "") => {
    const problem = validate(useFilters);
    if (problem) {
      setError(problem);
      return;
    }
    const legs = buildLegs(useFilters, useVariants).map((l) => ({ ...l, cursor: "", hasMore: false }));
    const sid = searchId || newSearchId();
    searchIdRef.current = sid;
    legsRef.current = legs;
    seenRef.current = new Set();
    titleRef.current = describeFilters(useFilters);
    setError("");
    setLoading(true);
    setSearched(true);
    setRows([]);
    setHasMore(false);
    setPicked(new Set());
    setOpenSummaries(new Set());
    setLegProgress({ done: 0, total: legs.length });
    setLegStats(legs.map((l, i) => ({ label: l.label, count: null, state: i === 0 ? "running" : "waiting" })));
    const stat = (i, patch) => setLegStats((prev) => prev.map((x, j) => (j === i ? { ...x, ...patch } : j === i + 1 && patch.state !== "running" ? { ...x, state: x.state === "waiting" ? "running" : x.state } : x)));
    for (let i = 0; i < legs.length; i += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const data = await fetchPage({ filters: legs[i].filters, limit: PAGE_SIZE, search_id: sid });
        if (searchIdRef.current !== sid) return;
        legs[i].cursor = data.next_cursor || "";
        legs[i].hasMore = Boolean(data.has_more && data.next_cursor);
        const added = appendRows(data.rows);
        stat(i, { count: added, state: "done" });
      } catch (err) {
        if (searchIdRef.current !== sid) return;
        const msg = err.status === 402 ? "You're out of credits. Top up to keep searching." : errorText(err);
        if (i === 0) setError(msg);
        else showToast(msg);
        stat(i, { state: "failed" });
        legs.splice(i); // the legs not run are dropped from paging
        break;
      } finally {
        if (searchIdRef.current === sid) {
          setLoading(false);
          setLegProgress({ done: Math.min(i + 1, legs.length), total: legs.length });
        }
      }
    }
    if (searchIdRef.current === sid) {
      setLegProgress({ done: legs.length, total: legs.length });
      setHasMore(legs.some((l) => l.hasMore));
    }
  };

  // Anything pasted -> the AI picks the filters -> the search runs on them straight away.
  const suggestAndSearch = async () => {
    const text = prompt.trim();
    if (text.length < 3) {
      setError("Paste or type a few words about who you are looking for.");
      return;
    }
    if (suggesting || loading) return;
    const sid = newSearchId();
    searchIdRef.current = sid;
    setSuggesting(true);
    setError("");
    setAiSummary("");
    setUnresolved([]);
    setSearched(true);
    setRows([]);
    setPicked(new Set());
    try {
      const res = await fetchWithAuth("/api/people-search/manual/suggest", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, search_id: sid }),
      });
      const data = await res.json().catch(() => ({}));
      if (searchIdRef.current !== sid) return;
      creditsChanged();
      if (!res.ok) {
        const err = new Error(data?.error || "Could not read that right now. Please try again.");
        err.status = res.status;
        throw err;
      }
      const next = data?.filters && typeof data.filters === "object" ? data.filters : {};
      const nextVariants = (Array.isArray(data?.variants) ? data.variants : [])
        .filter((v) => v && typeof v === "object" && v.patch && typeof v.patch === "object")
        .map((v) => ({ label: String(v.label || "Variant"), patch: v.patch, on: true }));
      setFilters(next);
      setVariants(nextVariants);
      setShown(new Set());
      setAiSummary(String(data?.summary || ""));
      setUnresolved(Array.isArray(data?.unresolved) ? data.unresolved : []);
      setSuggesting(false);
      await runSearch(next, nextVariants, sid);
    } catch (err) {
      if (searchIdRef.current !== sid) return;
      setSuggesting(false);
      setSearched(false);
      setError(err.status === 402 ? "You're out of credits. Top up to keep searching." : errorText(err));
    }
  };

  const onPromptKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      suggestAndSearch();
    }
  };

  const loadMore = async () => {
    if (loadingMore || legsBusy) return;
    const sid = searchIdRef.current;
    const legs = legsRef.current;
    const pending = legs.filter((l) => l.hasMore && l.cursor);
    if (pending.length === 0) return;
    setLoadingMore(true);
    try {
      for (const leg of pending) {
        // eslint-disable-next-line no-await-in-loop
        const data = await fetchPage({ filters: leg.filters, limit: PAGE_SIZE, search_id: sid, cursor: leg.cursor });
        if (searchIdRef.current !== sid) return;
        leg.cursor = data.next_cursor || "";
        leg.hasMore = Boolean(data.has_more && data.next_cursor);
        appendRows(data.rows);
      }
    } catch (err) {
      showToast(err.status === 402 ? "You're out of credits. Top up to keep searching." : errorText(err));
    } finally {
      if (searchIdRef.current === sid) {
        setHasMore(legs.some((l) => l.hasMore));
        setLoadingMore(false);
      }
    }
  };

  const clearAll = () => {
    setFilters({});
    setVariants([]);
    setShown(new Set());
    setAiSummary("");
    setUnresolved([]);
    setError("");
  };

  // Database membership: one POST / DELETE per person, reverted when the server says no.
  const toggleSaved = async (objId) => {
    if (!objId || busyIds.has(objId)) return;
    const isOn = savedIds.has(objId);
    const flip = (on) =>
      setSavedIds((prev) => {
        const next = new Set(prev);
        if (on) next.add(objId);
        else next.delete(objId);
        return next;
      });
    flip(!isOn);
    setBusyIds((prev) => new Set(prev).add(objId));
    try {
      const res = isOn
        ? await fetchWithAuth(`/api/outreach/external-shortlist/${encodeURIComponent(objId)}`, { method: "DELETE" })
        : await fetchWithAuth("/api/outreach/external-shortlist", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              objectId: objId,
              percentage_match: null,
              reqNo: `manual:${searchIdRef.current}`,
              searchTitle: titleRef.current,
            }),
          });
      if (!res.ok) throw new Error("Failed");
    } catch {
      flip(isOn);
      showToast(isOn ? "Couldn't remove them from your database." : "Couldn't add them to your database.");
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(objId);
        return next;
      });
    }
  };

  const addPicked = async () => {
    const targets = [...picked].filter((id) => !savedIds.has(id));
    if (targets.length === 0) return;
    let failed = 0;
    for (const id of targets) {
      // eslint-disable-next-line no-await-in-loop
      const res = await fetchWithAuth("/api/outreach/external-shortlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ objectId: id, percentage_match: null, reqNo: `manual:${searchIdRef.current}`, searchTitle: titleRef.current }),
      }).catch(() => ({ ok: false }));
      if (res.ok) setSavedIds((prev) => new Set(prev).add(id));
      else failed += 1;
    }
    setPicked(new Set());
    showToast(failed ? `Added ${targets.length - failed}; ${failed} couldn't be saved.` : `Added ${targets.length} to your database.`);
  };

  const togglePick = (id) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allPicked = rows.length > 0 && rows.every((r) => picked.has(r.object_id));
  const toggleAll = () => setPicked(allPicked ? new Set() : new Set(rows.map((r) => r.object_id).filter(Boolean)));
  const toSave = [...picked].filter((id) => !savedIds.has(id)).length;
  // Download the picked rows as a CSV, in the order they are listed.
  const exportPicked = () => {
    const out = rows
      .filter((r) => picked.has(r.object_id))
      .map((r, i) => {
        const { place, country } = locationCells(r.location, r.country);
        const languages = (r.languages || []).map((l) => (typeof l === "string" ? l : l?.name || "")).filter(Boolean).join(", ");
        const summary = String(r.summary || "").replace(/\s*\n\s*/g, " ").trim();
        return [i + 1, r.name || "", r.title || r.headline || "", r.company || "", place, country, r.link || "", languages, summary];
      });
    if (!out.length) return;
    // Search rows are thin profiles: no roles, schools or skills to export.
    downloadCsv(csvFileName("people-search"), toCsv(["#", "Name", "Title", "Company", "Location", "Country", "LinkedIn", "Languages", "Summary"], out));
  };

  const renderRow = (row) => {
    const id = row.object_id;
    const saved = savedIds.has(id);
    const subtitle = row.title ? (
      <>
        {row.title}
        {row.company && (
          <>
            {" at "}
            <b>{row.company}</b>
          </>
        )}
      </>
    ) : (
      row.headline
    );
    const { place, country } = placeOf(row);
    const languages = (row.languages || []).slice(0, 3);
    const summary = cleanSummary(row.summary);
    const open = openSummaries.has(id);
    const toggleSummary = () =>
      setOpenSummaries((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    return (
      <Row key={id || row.username} $picked={picked.has(id)}>
        <CheckBtn type="button" $on={picked.has(id)} onClick={() => togglePick(id)} aria-label={picked.has(id) ? "Deselect" : "Select"} disabled={!id}>
          {picked.has(id) ? <FiCheckSquare size={16} /> : <FiSquare size={16} />}
        </CheckBtn>
        <Person>
          <RankAvatar>
            <CandidatePhoto src={photoSrc(row)} fallback={<span>{initialsOf(row.name) || "?"}</span>} />
          </RankAvatar>
          <PersonText>
            <Name href={row.link || undefined} target="_blank" rel="noopener noreferrer" title="Open the LinkedIn profile">
              {row.name}
              {row.link && <BrandLinkedIn size={13} />}
            </Name>
            {subtitle && <Meta>{subtitle}</Meta>}
            {(place || country || languages.length > 0) && (
              <Facts>
                {(place || country) && (
                  <Fact title={row.location || country}>
                    {row.country_code ? <CircleFlag countryCode={row.country_code} height={13} width={13} alt="" /> : <FiMapPin size={11} />}
                    <span>
                      {place}
                      {place && country && <i> · </i>}
                      {country}
                    </span>
                  </Fact>
                )}
                {languages.length > 0 && (
                  <Fact title={`Speaks ${languages.join(", ")}`}>
                    <FiGlobe size={11} />
                    <span>{languages.join(", ")}</span>
                  </Fact>
                )}
              </Facts>
            )}
            {summary && (
              <>
                <Summary $open={open}>{summary}</Summary>
                {(open || summary.length > 140) && (
                  <MoreLess type="button" onClick={toggleSummary}>
                    {open ? "Less" : "More"}
                  </MoreLess>
                )}
              </>
            )}
          </PersonText>
        </Person>
        <RowActions>
          {id && actionsLocked ? (
            <ActionPills>
              <ActionPill type="button" style={{ opacity: 0.6 }} onClick={() => navigate("/plans")} title="Saving people to your database is part of the Starter plan and up">
                <FiLock size={12} style={{ color: "var(--secondary)" }} />
                <PillLabel>Database</PillLabel>
              </ActionPill>
            </ActionPills>
          ) : id ? (
            <ActionPills>
              <ActionPill
                type="button"
                $on={saved}
                disabled={busyIds.has(id)}
                onClick={() => toggleSaved(id)}
                title={saved ? "In your candidate database — click to remove" : "Add this person to your candidate database"}
              >
                {saved ? <FiCheck size={12} /> : <FiDatabase size={12} />}
                <PillLabel>Database</PillLabel>
              </ActionPill>
            </ActionPills>
          ) : null}
        </RowActions>
      </Row>
    );
  };

  const stage = !searched ? "idle" : suggesting || loading || legsBusy ? "working" : "done";
  const legsRunning = Math.min(legProgress.done + 1, legProgress.total);
  const step = suggesting
    ? ["Reading what you pasted", "Choosing the filters", "Finding other ways people write this job"][Math.min(stepTick, 2)]
    : legProgress.total > 1
      ? `Searching ${legsRunning} of ${legProgress.total} ways`
      : "Searching the people database";
  const shownCount = useCountUp(rows.length, stage === "done");
  const quote = prompt.trim().replace(/\s+/g, " ");
  const words = filterWords(filters);

  const promptBox = (
    <PromptCard>
      <PromptArea
        ref={promptRef}
        rows={1}
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        onKeyDown={onPromptKey}
        placeholder="Paste a job description, a headline, a few titles, a company list…"
        aria-label="Who are you looking for?"
      />
      <PromptFoot>
        <span>
          <kbd>Enter</kbd> to search, <kbd>Shift</kbd> + <kbd>Enter</kbd> for a new line
        </span>
        <PeopleButton $accent type="button" disabled={prompt.trim().length < 3} onClick={suggestAndSearch}>
          <ButtonContent>
            <Spark size={13} /> Find people
          </ButtonContent>
        </PeopleButton>
      </PromptFoot>
    </PromptCard>
  );

  // Back to the question, the paste still in the box, the filters kept.
  const newSearch = () => {
    searchIdRef.current = "";
    legsRef.current = [];
    setSearched(false);
    setRows([]);
    setError("");
    setLegProgress({ done: 0, total: 0 });
    setTimeout(() => promptRef.current?.focus(), 50);
  };

  const filtersButton = (
    <PeopleButton type="button" onClick={openFilters}>
      <ButtonContent>
        <FiSliders size={13} /> Filters {activeCount > 0 && <CountBadge>{activeCount}</CountBadge>}
      </ButtonContent>
    </PeopleButton>
  );

  const modal = filtersOpen
    ? createPortal(
        <Backdrop onMouseDown={(e) => e.target === e.currentTarget && setFiltersOpen(false)}>
          <Dialog role="dialog" aria-modal="true" aria-label="Filters">
            <DialogHead>
              <h3>Filters</h3>
              <CloseBtn type="button" onClick={() => setFiltersOpen(false)} aria-label="Close">
                <FiX size={16} />
              </CloseBtn>
            </DialogHead>
            <DialogSplit>
              <Nav aria-label="Filter groups">
                {variants.length > 0 && (
                  <>
                    <NavLabel>Searches</NavLabel>
                    <NavItem type="button" $active={modalTab === "variants"} onClick={() => setModalTab("variants")}>
                      Also searched as
                      <CountBadge>{variants.filter((v) => v.on !== false).length}</CountBadge>
                    </NavItem>
                    <NavLabel>Filters</NavLabel>
                  </>
                )}
                {SECTIONS.map((section) => {
                  const n = sectionCount(section, filters);
                  return (
                    <NavItem key={section.id} type="button" $active={modalTab === section.id} onClick={() => setModalTab(section.id)}>
                      {section.title}
                      {n > 0 && <CountBadge>{n}</CountBadge>}
                    </NavItem>
                  );
                })}
              </Nav>
              <Pane>
                {modalTab === "variants" ? (
                  <>
                    <PaneHead>
                      <div>
                        <h4>Also searched as</h4>
                        <p>Each of these is one more search on top of your filters, and the lists are combined into one. Click one to skip it, × to drop it.</p>
                      </div>
                      <GroupClear type="button" onClick={() => setVariants([])}>
                        Remove all
                      </GroupClear>
                    </PaneHead>
                    <VariantList>
                      {variants.map((v, i) => (
                        <VariantChip key={`${v.label}-${i}`} $on={v.on !== false} title={v.on !== false ? "Searched as well; click to skip it" : "Skipped; click to search it too"}>
                          <button type="button" onClick={() => setVariants((prev) => prev.map((x, j) => (j === i ? { ...x, on: x.on === false } : x)))}>
                            {v.label}
                          </button>
                          <button type="button" onClick={() => setVariants((prev) => prev.filter((_, j) => j !== i))} aria-label={`Remove ${v.label}`}>
                            <FiX size={12} />
                          </button>
                        </VariantChip>
                      ))}
                    </VariantList>
                  </>
                ) : (
                  (() => {
                    const section = SECTIONS.find((sec) => sec.id === modalTab) || SECTIONS[0];
                    const n = sectionCount(section, filters);
                    const hasMain = groupHasMain(section, filters);
                    const fields = groupFields(section, filters, shown, hasMain);
                    const mainFields = fields.filter((f) => !f.refine);
                    const moreFields = fields.filter((f) => f.refine);
                    const fieldSet = new Set(fields.map((f) => f.key));
                    const narrowing = hasMain ? section.fields.filter((f) => f.refine && !f.after && !fieldSet.has(f.key)) : [];
                    const dropField = (f) => {
                      setFilters((prev) => {
                        const next = { ...prev };
                        fieldKeys(f).forEach((k) => delete next[k]);
                        return next;
                      });
                      setShown((prev) => {
                        const next = new Set(prev);
                        next.delete(f.key);
                        return next;
                      });
                    };
                    const wide = (f) => f.kind === "chips" || f.kind === "range" || f.kind === "pair";
                    return (
                      <>
                        <PaneHead>
                          <div>
                            <h4>{section.title}</h4>
                            <p>{section.blurb}</p>
                          </div>
                          {n > 0 && (
                            <GroupClear type="button" onClick={() => section.fields.forEach(dropField)}>
                              Clear {n > 1 ? `${n} filters` : "filter"}
                            </GroupClear>
                          )}
                        </PaneHead>
                        {section.id === "role" && (filters.current_only === true || CURRENT_ONLY_AFFECTS.some((k) => isSet(filters[k]))) && (
                          <RailSwitch style={{ margin: "0 0 18px" }}>
                            <BoolField
                              field={{ ...CURRENT_ONLY_FIELD, note: "Otherwise any role in their career counts, for job title, companies, industries, company size, role description and the leave-outs." }}
                              value={filters.current_only}
                              onChange={(v) => setFilter("current_only", v)}
                            />
                          </RailSwitch>
                        )}
                        <PaneFields>
                          {mainFields.map((f) => (
                            <PaneItem key={f.key} $wide={wide(f)}>
                              <FilterField field={f} filters={filters} setFilter={setFilter} />
                            </PaneItem>
                          ))}
                        </PaneFields>
                        {(moreFields.length > 0 || narrowing.length > 0) && (
                          <PaneMore>
                            <PaneMoreTitle>Narrow it down</PaneMoreTitle>
                            {moreFields.length > 0 && (
                              <PaneFields style={{ marginBottom: narrowing.length ? 14 : 0 }}>
                                {moreFields.map((f) => (
                                  <PaneItem key={f.key} $wide={wide(f)}>
                                    <UnfoldItem>
                                      <FilterField field={f} filters={filters} setFilter={setFilter} />
                                      <RemoveField type="button" onClick={() => dropField(f)} aria-label={`Remove ${f.label}`} title="Remove this filter">
                                        <FiX size={13} />
                                      </RemoveField>
                                    </UnfoldItem>
                                  </PaneItem>
                                ))}
                              </PaneFields>
                            )}
                            {narrowing.length > 0 && (
                              <NarrowRow>
                                {narrowing.map((f) => (
                                  <AddChip key={f.key} type="button" onClick={() => setShown((prev) => new Set(prev).add(f.key))}>
                                    <FiPlus size={11} /> {f.label}
                                  </AddChip>
                                ))}
                              </NarrowRow>
                            )}
                          </PaneMore>
                        )}
                      </>
                    );
                  })()
                )}
              </Pane>
            </DialogSplit>
            <DialogFoot>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                <FootSummary>
                  <b>{activeCount}</b> {activeCount === 1 ? "filter" : "filters"}
                  {variants.length > 0 && (
                    <>
                      {" · "}
                      <b>{1 + variants.filter((v) => v.on !== false).length}</b> searches
                    </>
                  )}
                </FootSummary>
                <LinkBtn type="button" onClick={clearAll} disabled={activeCount === 0 && variants.length === 0}>
                  Clear all
                </LinkBtn>
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 12 }}>
                {error && <ErrorText role="alert">{error}</ErrorText>}
                <PeopleButton
                  $accent
                  type="button"
                  disabled={loading || suggesting || legsBusy || activeCount === 0}
                  onClick={() => {
                    if (validate(filters)) {
                      setError(validate(filters));
                      return;
                    }
                    setFiltersOpen(false);
                    setAiSummary("");
                    runSearch();
                  }}
                >
                  <ButtonContent>
                    <FiSearch size={13} /> {searched ? "Search again" : "Search"}
                  </ButtonContent>
                </PeopleButton>
              </span>
            </DialogFoot>
          </Dialog>
        </Backdrop>,
        document.body,
      )
    : null;

  return (
    <PeoplePage>
      <PeopleTopBar>
        <Crumbs>
          <Crumb as="span" style={{ cursor: "default" }}>
            Sourcing
          </Crumb>
          <CrumbSep>/</CrumbSep>
          <Crumb onClick={() => navigate("/find-candidates")}>People</Crumb>
          <CrumbSep>/</CrumbSep>
          <Crumb $current>Manual search</Crumb>
        </Crumbs>
      </PeopleTopBar>
      <PeopleCanvas>
        <PeopleScreen>
          <Tabs>
            <Tab type="button" onClick={() => navigate("/find-candidates")}>
              <Spark size={12} /> AI search
            </Tab>
            <Tab type="button" $active>
              <FiSliders size={12} /> Manual search
            </Tab>
          </Tabs>

          {stage === "idle" ? (
            <Hero>
              <Eyebrow style={{ justifyContent: "center" }}>
                <Spark size={12} /> Manual search
              </Eyebrow>
              <ScreenTitle>What are you finding today?</ScreenTitle>
              <ScreenSub>Paste anything about who you need. The search is set up for you and runs in seconds.</ScreenSub>
              {promptBox}
              {error && (
                <ErrorText role="alert" style={{ marginTop: 12 }}>
                  {error}
                </ErrorText>
              )}
              <HeroHint>
                Prefer to set it up yourself?
                <LinkBtn type="button" onClick={openFilters}>
                  Open the filters{activeCount > 0 ? ` (${activeCount})` : ""}
                </LinkBtn>
              </HeroHint>
            </Hero>
          ) : (
            <>
              {stage === "working" ? (
                <Scene>
                  <Aura />
                  <Working aria-live="polite">
                    <Orb>
                      <span />
                      <span />
                      <span />
                      <i>
                        <Spark size={20} />
                      </i>
                    </Orb>
                    <WorkingStep key={step}>{step}</WorkingStep>
                    {quote && (
                      <Quote style={{ marginTop: 14 }}>
                        <FiSearch size={11} />
                        <span>{quote}</span>
                      </Quote>
                    )}
                    {!suggesting && words.length > 0 && (
                      <WordRow>
                        {words.map((w, i) => (
                          <Word key={w} $i={i}>
                            {w}
                          </Word>
                        ))}
                      </WordRow>
                    )}
                    {!suggesting && legStats.length > 1 && (
                      <LegList>
                        {legStats.map((l, i) => (
                          <LegRow key={`${l.label}-${i}`} $state={l.state} $i={i}>
                            <LegMark $state={l.state}>
                              {l.state === "done" ? <FiCheck size={12} /> : l.state === "running" ? <i /> : l.state === "failed" ? <FiX size={12} /> : <b />}
                            </LegMark>
                            <span>{l.label}</span>
                            <span>
                              {l.state === "done" ? (
                                l.count > 0 ? (
                                  <>
                                    <b>+{l.count}</b> {l.count === 1 ? "person" : "people"}
                                  </>
                                ) : (
                                  "no one new"
                                )
                              ) : l.state === "running" ? (
                                "searching…"
                              ) : l.state === "failed" ? (
                                "didn't answer"
                              ) : (
                                ""
                              )}
                            </span>
                          </LegRow>
                        ))}
                      </LegList>
                    )}
                    <WorkingCount>
                      {rows.length > 0 ? (
                        <>
                          <b>{rows.length}</b> {rows.length === 1 ? "person" : "people"} so far
                        </>
                      ) : (
                        "\u00a0"
                      )}
                    </WorkingCount>
                  </Working>
                  {rows.length > 0 && (
                    <Crowd aria-hidden>
                      {rows.slice(0, 84).map((r, i) => (
                        <Face key={rowKey(r) || i} $i={i}>
                          <RankAvatar>
                            <CandidatePhoto src={photoSrc(r)} fallback={<span>{initialsOf(r.name) || "?"}</span>} />
                          </RankAvatar>
                        </Face>
                      ))}
                    </Crowd>
                  )}
                </Scene>
              ) : rows.length === 0 ? (
                <>
                  <Reveal>
                    <div>
                      <BigCount>
                        {error ? "Hmm." : "No one"}
                        {!error && <small>matched this</small>}
                      </BigCount>
                      <RevealSub>{error ? error : "Loosen a filter or two, or paste it differently and try again."}</RevealSub>
                    </div>
                    <RevealActions>
                      {filtersButton}
                      <PeopleButton type="button" onClick={newSearch}>
                        <ButtonContent>
                          <Spark size={13} /> New search
                        </ButtonContent>
                      </PeopleButton>
                    </RevealActions>
                  </Reveal>
                </>
              ) : (
                <>
                  <Reveal>
                    <div>
                      {quote && (
                        <Quote style={{ marginBottom: 12 }}>
                          <FiSearch size={11} />
                          <span>{quote}</span>
                        </Quote>
                      )}
                      <BigCount>
                        {shownCount.toLocaleString()}
                        <small>
                          {rows.length === 1 ? "person" : "people"} found{hasMore ? ", more available" : ""}
                        </small>
                      </BigCount>
                      {(aiSummary || unresolved.length > 0) && (
                        <RevealSub>
                          {aiSummary}
                          {unresolved.length > 0 && <small>Not in the company list, so left out: {unresolved.join(", ")}.</small>}
                        </RevealSub>
                      )}
                    </div>
                    <RevealActions>
                      {dirty && (
                        <PeopleButton $accent type="button" onClick={() => runSearch()}>
                          <ButtonContent>
                            <FiSearch size={13} /> Search again
                          </ButtonContent>
                        </PeopleButton>
                      )}
                      {filtersButton}
                      <PeopleButton type="button" onClick={newSearch}>
                        <ButtonContent>
                          <Spark size={13} /> New search
                        </ButtonContent>
                      </PeopleButton>
                    </RevealActions>
                  </Reveal>
                  <FadeList>
                    <ResultsBar>
                      <span>
                        <b>{rows.length}</b> shown
                      </span>
                      <LinkBtn type="button" onClick={toggleAll}>
                        {allPicked ? "Clear selection" : "Select all shown"}
                      </LinkBtn>
                    </ResultsBar>
                    {picked.size > 0 && (
                      <BulkBar>
                        <span>{picked.size} selected</span>
                        {actionsLocked ? (
                          <PeopleButton $sm type="button" onClick={() => navigate("/plans")} title="Saving people to your database is part of the Starter plan and up">
                            <FiLock size={12} /> Add to database
                          </PeopleButton>
                        ) : (
                          <PeopleButton $sm $accent type="button" disabled={toSave === 0} onClick={addPicked}>
                            <FiDatabase size={12} /> {toSave === 0 ? "All in database" : `Add ${toSave} to database`}
                          </PeopleButton>
                        )}
                        {actionsLocked ? (
                          <PeopleButton $sm type="button" onClick={() => navigate("/plans")} title="Exporting people is part of the Starter plan and up">
                            <FiLock size={12} /> Export CSV
                          </PeopleButton>
                        ) : (
                          <PeopleButton $sm type="button" onClick={exportPicked} title="Download the selected people as a CSV file">
                            <FiDownload size={12} /> Export CSV
                          </PeopleButton>
                        )}
                        <LinkBtn type="button" onClick={() => setPicked(new Set())}>
                          Clear
                        </LinkBtn>
                      </BulkBar>
                    )}
                    <List>{rows.map(renderRow)}</List>
                    <Foot>
                      {hasMore ? (
                        <PeopleButton type="button" disabled={loadingMore} onClick={loadMore}>
                          {loadingMore ? "Loading…" : "Load more"}
                        </PeopleButton>
                      ) : (
                        <span>That's everyone who matched.</span>
                      )}
                    </Foot>
                  </FadeList>
                </>
              )}
            </>
          )}
        </PeopleScreen>
      </PeopleCanvas>
      {modal}
    </PeoplePage>
  );
}
